import { BUSINESS_TYPES, type BusinessType } from '../api/endpoints'
import { MAX_TYPE_NOTE, SUBTYPES, businessTypeHint, businessTypeName } from '../businessTypes'
import { t } from '../strings'

/** `type` is empty until the person picks one: no kind of business is chosen for them. */
export type TypeChoice = { type: BusinessType | ''; subtype: string; note: string }

/**
 * The four kinds of business as short cards. Only the chosen one is open: it says what is different about that kind,
 * then offers the optional sub-type (or, for "Lainnya", a free-text note). Changing the type drops the sub-type.
 */
export function BusinessTypePicker({
  value,
  onChange,
  missing = false,
}: {
  value: TypeChoice
  onChange: (next: TypeChoice) => void
  /** True once the person tried to go on without choosing. */
  missing?: boolean
}) {
  return (
    <fieldset className="choice">
      <legend>{t.businessType}</legend>
      {BUSINESS_TYPES.map((type) => {
        const on = value.type === type
        return (
          <div key={type}>
            <label className={on ? 'choice-option on' : 'choice-option'}>
              <input
                type="radio"
                name="business_type"
                value={type}
                checked={on}
                onChange={() => onChange({ ...value, type, subtype: '' })}
              />
              <span>
                <strong>{businessTypeName(type)}</strong>
                <span className="muted small">{businessTypeHint(type)}</span>
              </span>
            </label>
            {on && (
              <div className="type-detail">
                <ul>
                  {t.businessExplain[type].map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                {type === 'other' ? (
                  <label>
                    {t.businessTypeNote}
                    <input
                      name="business_type_note"
                      maxLength={MAX_TYPE_NOTE}
                      value={value.note}
                      onChange={(e) => onChange({ ...value, note: e.target.value })}
                    />
                  </label>
                ) : (
                  <>
                    <div className="small">
                      <strong>{t.businessSubtypeTitle}</strong>
                    </div>
                    <div className="chips">
                      {SUBTYPES[type].map((key) => (
                        <button
                          key={key}
                          type="button"
                          className={value.subtype === key ? 'chip on' : 'chip'}
                          aria-pressed={value.subtype === key}
                          // Choosing the chosen one again clears it: the sub-type is optional.
                          onClick={() => onChange({ ...value, subtype: value.subtype === key ? '' : key })}
                        >
                          {t.businessSubtypes[key]}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )
      })}
      {missing && value.type === '' && (
        <p className="error" role="alert">
          {t.businessTypeRequired}
        </p>
      )}
      <p className="muted small">{t.businessChangeLater}</p>
    </fieldset>
  )
}
